import React, { useRef, useState } from "react";
// @ts-ignore
import AvatarEditor from "react-avatar-editor";
import { useTranslation } from "react-i18next";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  Slider,
} from "@mui/material";
import CloseIcon from "@iconify/icons-mdi/close";
import ImageIcon from "@iconify/icons-mdi/image-size-select-actual";
import { InlineIcon } from "@iconify/react";
import axios from "axios";
import { OcCard, OcDropzoneArea } from "oc/components";
import useStyles from "oc/components/info-card-item/info-card-item.style";
import { useOcApi, useOcAvatar, useOcSnackbar } from "oc/hooks";

const dataURLtoFile = (dataurl: string, filename: string): File => {
  const arr = dataurl.split(",");
  const mimeMatch = arr[0].match(/^data:([^;]+);/);
  const mime = mimeMatch ? mimeMatch[1] : "";
  const b64str = window.atob(arr[1]);
  let n = b64str.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    const cp = b64str.codePointAt(n) ?? 0;
    u8arr[n] = cp & 0xff;
  }
  return new File([u8arr], filename, { type: mime });
};

type MyHrProfileImageProps = {
  loading: boolean;
  disabledNewRequest: boolean;
  employeeCode: string;
  isMobile: boolean;
};

export const MyHrProfileImage = ({
  loading,
  disabledNewRequest,
  employeeCode,
  isMobile,
}: MyHrProfileImageProps) => {
  const { t } = useTranslation();
  const { uploadUrl } = useOcApi();
  const { data, refetch } = useOcAvatar({ employee: employeeCode });

  const [showImageEdit, setshowImageEdit] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [scale, setScale] = useState(1);
  const [transformedFile, setTransformedFile] = useState<File | null>(null);

  const classes = useStyles({ isMobile });
  const transformComponentRef = useRef<any>(null);
  const editorRef = useRef<AvatarEditor>(null);

  const { setOpenMessage } = useOcSnackbar();

  const handleImageChange = () => {
    if (!transformedFile) {
      setTransformedFile && setTransformedFile(image);
    } else if (editorRef.current) {
      const newImage = editorRef.current.getImageScaledToCanvas().toDataURL();
      const newImagefile = dataURLtoFile(newImage, image?.name || "");
      if (newImagefile.size !== transformedFile?.size) {
        setTransformedFile && setTransformedFile(newImagefile);
      }
    }
  };

  const handleShowNewItemDialog = () => {
    if (showImageEdit) {
      setshowImageEdit(false);
      setScale(1);
    } else {
      setshowImageEdit(true);
    }
  };

  const handleUpload = async () => {
    handleImageChange();

    const formData = new FormData();
    formData.append("employeeCode", employeeCode);
    formData.append("type", "avatar");

    if (scale === 1) {
      if (image) {
        formData.append("file", image);
      }
    } else if (scale > 1 && transformedFile) {
      formData.append("file", transformedFile);
    }

    try {
      await axios({
        method: "post",
        url: uploadUrl,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      refetch();
      setImage(null);
      setTransformedFile(null);
      setOpenMessage({
        type: "success",
        message: t("MY_HR_PROFILE_IMAGE_UPLOAD_SUCCEEDED"),
      });
      handleShowNewItemDialog();
    } catch (error) {
      setOpenMessage({
        type: "error",
        message: t("MY_HR_PROFILE_PROFILE_IMAGE_UPLOAD_FAILED"),
      });
      console.log(error);
    }
  };

  const updateScale = (e: Event, newScaleValue: number | number[]) => {
    const targetScale = Array.isArray(newScaleValue)
      ? Number.parseFloat(newScaleValue[0] as unknown as string)
      : Number.parseFloat(newScaleValue as unknown as string);
    const factor = Math.log(targetScale / scale);
    const { zoomIn, zoomOut } = transformComponentRef.current;

    if (targetScale > scale) {
      zoomIn(factor, 0);
    } else {
      zoomOut(-factor, 0);
    }

    setScale(targetScale);
  };

  const cancelUpload = () => {
    setImage(null);
    setScale(1);
    handleShowNewItemDialog();
  };

  const handleSelectImage = (file: File | File[]) => {
    const selectedFile = Array.isArray(file) ? file[0] : file;
    if (!selectedFile) return;
    if (selectedFile.size > 8000000) {
      setOpenMessage({
        type: "error",
        message: t("MY_HR_PROFILE_IMAGE_UPLOAD_SIZE_FAILED"),
      });
      return;
    }
    setImage(selectedFile);
  };

  return (
    <OcCard label={t("MY_HR_PROFILE_IMAGE")}>
      <>
        <Box height="49px" />
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Avatar
            id="avatar-img"
            alt="avatar-image"
            src={data}
            style={{
              width: "252px",
              height: "252px",
            }}
          />
        </Box>
        <Box height="42px" />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          padding="16px 0px"
        >
          <Button
            id="profile-image-edit"
            variant="contained"
            color="primary"
            onClick={handleShowNewItemDialog}
            disableElevation
            disabled={loading || disabledNewRequest}
          >
            {t("MY_HR_PROFILE_IMAGE_EDIT")}
          </Button>
          <Dialog
            open={showImageEdit}
            fullScreen={isMobile ? true : false}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                return false;
              }
              handleShowNewItemDialog();
            }}
            disableEscapeKeyDown
            scroll="body"
            PaperProps={{
              style: {
                padding: "0px 24px 24px 24px",
                width: isMobile ? "auto" : 600,
                display: isMobile ? "flex" : "inline-block",
                flexDirection: "column",
              },
            }}
          >
            {isMobile && (
              <Box position="absolute" right="16px" top="16px">
                <InlineIcon
                  icon={CloseIcon}
                  width="24px"
                  onClick={cancelUpload}
                  style={{
                    cursor: "pointer",
                  }}
                />
              </Box>
            )}
            <div
              style={{
                paddingTop: 36,
                paddingLeft: 0,
                paddingRight: 0,
                textAlign: "center",
                fontSize: 24,
                fontFamily: "Mulish",
              }}
            >
              {t("MY_HR_IMAGE_UPLOAD")}
            </div>
            <Box height="26px" />
            <DialogActions>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <OcDropzoneArea
                  dropzoneText={t("MY_HR_DRAG_AND_DROP")}
                  acceptedFiles={{
                    "image/*": [".png", ".gif", ".jpeg", ".jpg"],
                  }}
                  onChange={handleSelectImage}
                />
                <Box height="36px" />
                <Collapse in={!!image && !!image.name && image.name.length > 0}>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      paddingBottom: "16px",
                      background: "#666666",
                      borderRadius: "8px",
                      height: isMobile ? "70%" : "auto",
                    }}
                  >
                    <TransformWrapper
                      ref={transformComponentRef}
                      centerOnInit={true}
                      doubleClick={{ disabled: true }}
                      panning={{ disabled: true }}
                      minScale={1}
                      maxScale={1}
                    >
                      <TransformComponent>
                        <Box
                          style={{
                            maxHeight: isMobile ? "auto" : "383px",
                            marginTop: "-17px",
                            marginLeft: "-17px",
                            marginRight: "-17px",
                          }}
                        >
                          <Box
                            style={{
                              border: "6px solid white",
                              borderRadius: "50%",
                              height: isMobile ? "auto" : "354px",
                              width: isMobile ? "auto" : "354px",
                              marginTop: "17px",
                              marginLeft: "17px",
                              position: "absolute",
                              pointerEvents: "none",
                            }}
                          ></Box>
                          <AvatarEditor
                            ref={editorRef}
                            image={image}
                            width={isMobile ? 200 : 500}
                            scale={scale}
                            height={isMobile ? 200 : 500}
                            color={[102, 102, 102, 0.6]}
                            borderRadius={300}
                            rotate={0}
                            style={{
                              maxHeight: isMobile ? "auto" : "400px",
                              maxWidth: isMobile ? "auto" : "400px",
                            }}
                            onImageChange={handleImageChange}
                          />
                        </Box>
                      </TransformComponent>
                    </TransformWrapper>
                  </Box>
                  <Box height="54px" />
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box>
                      <InlineIcon
                        icon={ImageIcon}
                        width="24px"
                        style={{
                          color: "#4d4d4d",
                          width: "16px",
                          height: "13px",
                          marginRight: "16px",
                        }}
                      />
                    </Box>
                    <Slider
                      className={classes.input}
                      defaultValue={1}
                      min={1}
                      max={2.5}
                      step={0.01}
                      onChange={updateScale}
                    />
                    <Box>
                      <InlineIcon
                        icon={ImageIcon}
                        width="24px"
                        style={{
                          color: "#4d4d4d",
                          width: "44px",
                          height: "36px",
                          marginLeft: "16px",
                        }}
                      />
                    </Box>
                  </Box>
                  <Box height="54px" />
                </Collapse>
                <Button
                  id="upload-image"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={handleUpload}
                  disabled={!image}
                >
                  {t("OC_UPLOAD")}
                </Button>
                <Box height="43px" />
                <Button
                  id="cancel-upload"
                  fullWidth
                  onClick={cancelUpload}
                  variant="text"
                  color="secondary"
                >
                  {t("OC_CANCEL")}
                </Button>
                <Box height="11px" />
              </div>
            </DialogActions>
          </Dialog>
        </Box>
        <Box height="28px" />
      </>
    </OcCard>
  );
};

export default MyHrProfileImage;
